/**
 * Full Pricing block — display-only informational summary of every price
 * tier for a product (standard monthly rate + 3-month plan).
 * Placed on TreatmentDetail pages below the savings note.
 *
 * All plan selection and billing happen off-site in the Bask intake flow;
 * this component is purely for transparency so patients can see what each
 * cadence costs before starting a visit.
 */

import { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";

function money(n) {
  return `$${n.toLocaleString("en-US")}`;
}

function RenewalNote({ billed, testId }) {
  return (
    <span data-testid={testId} className="mt-0.5 text-[11px] text-[#8A9A92] tabular-nums leading-tight">
      {billed}.
    </span>
  );
}

function PricingRows({ tiers, slug, optionKey = "", renewal = false }) {
  const idPrefix = optionKey
    ? `full-pricing-${slug}-${optionKey}`
    : `full-pricing-${slug}`;

  return (
    <div className="divide-y divide-[#E8E5DF]">
      {tiers.monthly !== undefined && (
        <div
          data-testid={`${idPrefix}-monthly`}
          className="flex flex-col py-2.5"
        >
          <span className="text-sm text-[#5E5C58]">Monthly</span>
          <span className="font-display text-base font-semibold text-[#2C2B29] tabular-nums leading-tight">
            {money(tiers.monthly)}
            <span className="ml-0.5 text-[11px] font-normal text-[#8A9A92]">/month</span>
          </span>
          {renewal && <RenewalNote billed="Billed monthly" testId={`${idPrefix}-monthly-renewal`} />}
        </div>
      )}
      {tiers.threeMonth && (
        <div
          data-testid={`${idPrefix}-3month`}
          className="flex flex-col py-2.5"
        >
          <span className="text-sm text-[#5E5C58]">
            3-Month Plan
            <span className="ml-1.5 text-[#C96A4E] font-semibold"> — Best Value</span>
          </span>
          <span className="font-display text-base font-semibold text-[#C96A4E] tabular-nums leading-tight">
            {money(tiers.threeMonth.perMonth)}
            <span className="ml-0.5 text-[11px] font-normal text-[#8A9A92]">/month</span>
          </span>
          {renewal ? (
            <RenewalNote
              billed={`Billed ${money(tiers.threeMonth.total)} every 3 months`}
              testId={`${idPrefix}-3month-renewal`}
            />
          ) : (
            <span className="mt-0.5 text-[11px] text-[#8A9A92] tabular-nums leading-tight">
              {money(tiers.threeMonth.total)} billed every 3 months
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default function FullPricing({ treatment, showRenewal = false }) {
  const fp = treatment?.fullPricing;
  const ref = useRef(null);
  const { hash } = useLocation();

  useEffect(() => {
    if (!ref.current) return;
    ref.current.open = false;
    if (!hash.startsWith("#pricing-") || !ref.current.querySelector(hash)) return;
    const el = ref.current;
    const id = setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "center" }), 120);
    return () => clearTimeout(id);
  }, [hash, treatment?.slug]);

  if (!fp) return null;

  const options = Array.isArray(fp.options) ? fp.options : null;

  return (
    <details
      ref={ref}
      data-testid={`full-pricing-${treatment.slug}`}
      className="group mt-4 bg-white/70 backdrop-blur-sm border border-[#E8E5DF] rounded-2xl overflow-hidden"
    >
      <summary
        data-testid={`full-pricing-toggle-${treatment.slug}`}
        className="flex items-center justify-between gap-2 px-4 py-2 cursor-pointer list-none select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C96A4E]/30 hover:bg-[#FAF9F6]/60 transition-colors"
      >
        <span
          id={`full-pricing-heading-${treatment.slug}`}
          className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#C96A4E]"
        >
          Compare Plans & Pricing
        </span>
        <span
          aria-hidden="true"
          className="inline-flex items-center justify-center w-5 h-5 rounded-full border border-[#E8E5DF] text-[#5E5C58] text-[11px] leading-none transition-transform duration-200 group-open:rotate-180"
        >
          ▾
        </span>
      </summary>

      <div className="px-5 pt-1 pb-4">
        {showRenewal && (
          <p
            data-testid={`full-pricing-renewal-${treatment.slug}`}
            className="mb-3 text-[11px] text-[#566B5D] leading-tight"
          >
            All plans renew automatically until canceled.{" "}
            <Link to="/legal/refund" className="font-semibold underline underline-offset-2 hover:text-[#C96A4E]">
              Cancel anytime
            </Link>
            .
          </p>
        )}
        {options ? (
        (() => {
          const dividedSlugs = new Set(["glp-nad-bundles", "vitality", "weight-loss"]);
          const isDivided = dividedSlugs.has(treatment.slug);
          return (
            <div className={isDivided ? "divide-y divide-[#C96A4E]" : "space-y-4"}>
              {options.map((opt) => {
                const key = opt.label.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                return (
                  <div key={opt.label} id={`pricing-${key}`} className={`scroll-mt-28 ${isDivided ? "py-4 first:pt-0 last:pb-0" : ""}`}>
                    <p className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.14em] text-[#8A9A92] mb-2">
                      {opt.label}
                    </p>
                    <PricingRows tiers={opt} slug={treatment.slug} optionKey={key} renewal={showRenewal} />
                  </div>
                );
              })}
            </div>
          );
        })()
      ) : (
        <PricingRows tiers={fp} slug={treatment.slug} renewal={showRenewal} />
      )}
      </div>
    </details>
  );
}
